import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../../utils/apiConfig';
import './Products.css';

const SoldProductForm = ({ onSuccess }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    itemCode: '',
    itemName: '',
    hsn: '',
    gst: 0,
    unitPrice: 0,
    quantity: 1,
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [allSoldItems, setAllSoldItems] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSoldItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check authentication first
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      
      
      const [cashBillsRes, creditBillsRes, soldProductsRes] = await Promise.allSettled([
        axios.get(`${API_CONFIG.BASE_URL}/api/cashbills`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_CONFIG.BASE_URL}/api/creditbills`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_CONFIG.BASE_URL}/api/sold-products`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      

      let allItems = [];

      // Process cash bills - extract product details
      if (cashBillsRes.status === 'fulfilled' && cashBillsRes.value.data) {
        const cashBillsData = cashBillsRes.value.data || [];
        
        // Extract product details from each bill's items array
        const cashBillProducts = [];
        cashBillsData.forEach(bill => {
          
          if (bill.items && Array.isArray(bill.items)) {
            bill.items.forEach((item, itemIndex) => {
              
              // Map fields from cash bill items
              const itemCode = item.code || item.itemCode || item.productCode || item.sku || '';
              const itemName = item.itemname || item.itemName || item.description || item.productName || item.name || '';
              const hsn = item.hsnSac || item.hsn || item.sac || item.hsnCode || item.taxCode || '';
              const gst = item.taxRate || item.gstRate || item.cgstRate || item.sgstRate || item.gst || item.tax || 0;
              const quantity = item.quantity || item.qty || item.amount || item.count || item.unit || 1;
              const rate = item.rate || item.unitPrice || item.unitRate || item.price || 0;
              
              
              if (itemName && itemName.trim() !== '') {
                cashBillProducts.push({
                  id: `${bill.id || bill._id}-item-${itemIndex}`,
                  itemCode,
                  itemName,
                  hsn,
                  gst,
                  quantity,
                  rate,
                  source: 'CashBill',
                  invoice: bill.invoiceNumber || bill.invoice || bill.billNumber || bill.invoiceNo || 'N/A',
                  date: bill.createdAt || bill.date || bill.billDate || bill.createdDate,
                  customerName: bill.customerName || 'N/A'
                });
              }
            });
          }
        });
        
        allItems = [...allItems, ...cashBillProducts];
      }

      // Process credit bills - extract product details
      if (creditBillsRes.status === 'fulfilled' && creditBillsRes.value.data) {
        const creditBillsData = creditBillsRes.value.data.data || creditBillsRes.value.data || [];
        
        // Extract product details from each bill's items array
        const creditBillProducts = [];
        creditBillsData.forEach(bill => {
          
          if (bill.items && Array.isArray(bill.items)) {
            bill.items.forEach((item, itemIndex) => {
              
              // Map fields from credit bill items
              const itemCode = item.code || item.itemCode || item.productCode || item.sku || '';
              const itemName = item.description || item.itemName || item.itemname || item.productName || item.name || '';
              const hsn = item.hsnSac || item.hsn || item.sac || item.hsnCode || item.taxCode || '';
              const gst = item.taxRate || item.gstRate || item.cgstRate || item.sgstRate || item.gst || item.tax || 0;
              const quantity = item.quantity || item.qty || item.amount || item.count || item.unit || 1;
              const rate = item.rate || item.unitPrice || item.unitRate || item.price || 0;
              
              if (itemName && itemName.trim() !== '') {
                creditBillProducts.push({
                  id: `${bill.id || bill._id}-item-${itemIndex}`,
                  itemCode,
                  itemName,
                  hsn,
                  gst,
                  quantity,
                  rate,
                  source: 'CreditBill',
                  invoice: bill.invoiceNumber || bill.invoice || bill.billNumber || bill.invoiceNo || 'N/A',
                  date: bill.createdAt || bill.date || bill.billDate || bill.createdDate,
                  customerName: bill.customerName || 'N/A'
                });
              }
            });
          }
        });
        
        allItems = [...allItems, ...creditBillProducts];
      }

      // Process manual entries from sold-products collection
      if (soldProductsRes.status === 'fulfilled' && soldProductsRes.value.data) {
        const soldProductsData = soldProductsRes.value.data.data || soldProductsRes.value.data || [];
        console.log('=== MANUAL ENTRIES DEBUG ===');
        console.log('Sold Products Raw Data:', soldProductsData);
        console.log('Sold Products Data Type:', typeof soldProductsData);
        console.log('Sold Products Is Array:', Array.isArray(soldProductsData));
        console.log('Sold Products Length:', soldProductsData?.length);
        
        // Filter for manual entries
        const manualEntries = soldProductsData.filter(product => 
          product.source === 'Manual Entry' || 
          product.entryType === 'Manual' || 
          product.isManualEntry === true
        );
        
        console.log('Manual Entries Found:', manualEntries.length);
        console.log('Manual Entries:', manualEntries);
        
        // Convert manual entries to table format
        const manualEntryProducts = manualEntries.map((product, index) => ({
          id: product.id || `manual-${index}`,
          itemCode: product.itemCode || '',
          itemName: product.itemName || '',
          hsn: product.hsn || '',
          gst: product.gst || 0,
          quantity: product.quantity || 1,
          rate: product.unitRate || product.unitPrice || 0,
          source: 'Manual Entry',
          invoice: `MAN-${product.id?.slice(-6) || 'N/A'}`,
          date: product.createdAt || product.soldDate || new Date(),
          customerName: product.addedBy || 'Purchase Admin'
        }));
        
        console.log('Processed Manual Entries:', manualEntryProducts);
        allItems = [...allItems, ...manualEntryProducts];
        console.log('=== END MANUAL ENTRIES DEBUG ===');
      }

      console.log('Final Processed Items:', allItems);
      console.log('Final Items Count:', allItems.length);
      console.log('Sample Final Item:', allItems[0]);
      
      // Check if we have any valid data
      const validItems = allItems.filter(item => 
        item.itemName && item.itemName.trim() !== '' && 
        item.itemCode && item.itemCode.trim() !== ''
      );
      
      console.log('Valid Items Count:', validItems.length);
      console.log('Valid Items:', validItems);
      
      // Use valid items if available, otherwise show empty state
      if (validItems.length > 0) {
        setAllSoldItems(validItems);
      } else if (allItems.length > 0) {
        setAllSoldItems(allItems);
      } else {
        setAllSoldItems([]);
      }
      
    } catch (err) {
      console.error('Error fetching sold items:', err);
      setError(err.response?.data?.message || 'Failed to fetch sold items');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllProducts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }
      
      const response = await axios.get(`${API_CONFIG.BASE_URL}/api/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fetchedProducts = response.data;
      const productsArray = Array.isArray(fetchedProducts) ? fetchedProducts : (fetchedProducts.data || fetchedProducts);
      
      
      setAllProducts(productsArray || []);
    } catch (err) {
      // Don't set error for this as it's not critical
    }
  }, []);

  useEffect(() => {
    fetchSoldItems();
    fetchAllProducts();
  }, [fetchSoldItems, fetchAllProducts]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSuggestions && !event.target.closest('.spf-field')) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSuggestions]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    
    // Auto-fill and show suggestions when product name is entered
    if (name === 'itemName') {
      if (value.trim()) {
        // Show filtered suggestions based on input
        const filteredProducts = allProducts.filter(product => 
          product.itemName && product.itemName.toLowerCase().includes(value.toLowerCase())
        ).slice(0, 10); // Show up to 10 suggestions
        
        setSuggestions(filteredProducts);
        setShowSuggestions(filteredProducts.length > 0);
        
        // Auto-fill if exact match
        const exactMatch = allProducts.find(product => 
          product.itemName && product.itemName.toLowerCase().trim() === value.toLowerCase().trim()
        );
        
        if (exactMatch) {
          setForm(prev => ({
            ...prev,
            itemCode: exactMatch.itemCode || prev.itemCode,
            hsn: exactMatch.hsn || prev.hsn,
            gst: exactMatch.gst || prev.gst,
            unitPrice: exactMatch.unitPrice || exactMatch.unitRate || prev.unitPrice,
            itemName: value
          }));
          setShowSuggestions(false);
        }
      } else {
        // If field is empty, show all products
        setSuggestions(allProducts.slice(0, 20)); // Show first 20 products
        setShowSuggestions(allProducts.length > 0);
      }
    }
  };

  const handleSuggestionClick = (product) => {
    setForm(prev => ({
      ...prev,
      itemName: product.itemName,
      itemCode: product.itemCode || prev.itemCode,
      hsn: product.hsn || prev.hsn,
      gst: product.gst || prev.gst,
      unitPrice: product.unitPrice || product.unitRate || prev.unitPrice
    }));
    setShowSuggestions(false);
  };

  const handleItemNameClick = () => {
    // Show all products when clicking on Item Name field
    if (allProducts.length > 0) {
      setSuggestions(allProducts.slice(0, 20)); // Show first 20 products
      setShowSuggestions(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);


    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }
      

    const data = {
      itemCode: form.itemCode,
      itemName: form.itemName,
      hsn: form.hsn,
      gst: parseFloat(form.gst) || 0,
      unitRate: parseFloat(form.unitPrice) || 0,
      quantity: parseInt(form.quantity, 10) || 1,
      source: 'Manual Entry',
      entryType: 'Manual',
      addedBy: 'Purchase Admin',
      soldDate: new Date().toISOString(),
      status: 'Sold'
    };


if (
      !data.itemCode ||
      !data.itemName ||
      !data.hsn ||
      data.gst < 0 ||
      data.unitRate < 0 ||
      data.quantity < 1
    ) {
      setError(
        'All fields (Item Code, Item Name, HSN) are required. GST, Unit Rate must be non-negative, and Quantity must be at least 1.'
      );
      setLoading(false);
      return;
    }
    


      const response = editingProduct
        ? await axios.put(`${API_CONFIG.BASE_URL}/api/sold-products/${editingProduct._id}`, data, {
            headers: { Authorization: `Bearer ${token}` }
          })
        : await axios.post(`${API_CONFIG.BASE_URL}/api/sold-products`, data, {
            headers: { Authorization: `Bearer ${token}` }
          });


      if (response.data.success) {
        console.log('Sale recorded successfully!');
        setForm({ itemCode: '', itemName: '', hsn: '', gst: 0, unitPrice: 0, quantity: 1 });
        setEditingProduct(null);
        // Show success message
        alert('✅ Manual sale recorded successfully! The product has been added to sold products.');
        // Refresh the sold items list
        fetchSoldItems();
        if (onSuccess) onSuccess();
      } else {
        console.error('API returned success: false', response.data);
        setError(response.data.message || 'Failed to save sale');
      }
    } catch (err) {
      console.error('=== SALE SUBMISSION ERROR ===');
      console.error('Error object:', err);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      console.error('=== END ERROR DEBUG ===');
      setError(err.response?.data?.message || err.message || 'Failed to save sale');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setForm({ itemCode: '', itemName: '', hsn: '', gst: 0, unitPrice: 0, quantity: 1 });
    setEditingProduct(null);
  };

  // Calculate sold products totals
  const totalSoldProducts = allSoldItems.length;
  const totalSoldQuantity = allSoldItems.reduce((sum, item) => {
    return sum + (parseFloat(item.quantity) || 0);
  }, 0);
  const totalSoldValue = allSoldItems.reduce((sum, item) => {
    const rate = parseFloat(item.rate) || 0;
    const quantity = parseFloat(item.quantity) || 0;
    return sum + (rate * quantity);
  }, 0);

  // Calculate totals by source
  const sourceTotals = allSoldItems.reduce((acc, item) => {
    const source = item.source || 'Unknown';
    if (!acc[source]) {
      acc[source] = { count: 0, quantity: 0, value: 0 };
    }
    acc[source].count += 1;
    acc[source].quantity += parseFloat(item.quantity) || 0;
    acc[source].value += (parseFloat(item.rate) || 0) * (parseFloat(item.quantity) || 0);
    return acc;
  }, {});

  return (
    <div className="spf-wrapper">
      <button 
        className="bck-button" 
        onClick={() => navigate(-1)}
        style={{
          backgroundColor: '#997a8d',
          color: 'white',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '5px',
          cursor: 'pointer',
          fontSize: '14px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}
      >
        ← Back
      </button>
      <div className="spf-form-section">
        <h2>{editingProduct ? 'Edit Sale Record' : 'Record New Sale'}</h2>
        <form onSubmit={handleSubmit} className="spf-grid">
          {['itemCode', 'itemName', 'hsn', 'gst', 'unitPrice', 'quantity'].map((field) => (
            <div className="spf-field" key={field} style={{ position: 'relative' }}>
              <label>{field.replace(/([A-Z])/g, ' $1').toUpperCase()}:</label>
              <input
                type={field === 'gst' || field === 'unitPrice' || field === 'quantity' ? 'number' : 'text'}
                name={field}
                value={form[field] || ''}
                onChange={handleFormChange}
                onClick={field === 'itemName' ? handleItemNameClick : undefined}
                required
                autoComplete="off"
                placeholder={field === 'itemName' ? 'Click to see all products or type to search...' : ''}
              />
              
              {/* Product dropdown for itemName */}
              {field === 'itemName' && showSuggestions && suggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  <div style={{
                    padding: '8px 12px',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #eee',
                    fontSize: '12px',
                    color: '#666',
                    fontWeight: 'bold'
                  }}>
                    {suggestions.length} products found - Click to select
                  </div>
                  {suggestions.map((product, index) => (
                    <div
                      key={product.id || index}
                      onClick={() => handleSuggestionClick(product)}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        fontSize: '14px',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#e3f2fd'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      <div style={{ fontWeight: 'bold', color: '#333', marginBottom: '2px' }}>
                        {product.itemName}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', display: 'flex', gap: '15px' }}>
                        <span>Code: {product.itemCode || 'N/A'}</span>
                        <span>HSN: {product.hsn || 'N/A'}</span>
                        <span>GST: {product.gst || 0}%</span>
                        <span>Price: ₹{product.unitPrice || product.unitRate || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div className="spf-form-buttons">
            <button type="submit" className="spf-btn spf-submit" disabled={loading}>
              {loading ? 'Saving...' : editingProduct ? 'Update Sale' : 'Record Sale'}
            </button>
            {editingProduct && (
              <button type="button" className="spf-btn spf-cancel" onClick={handleCancelEdit} disabled={loading}>
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Bills Data Table - Only showing required fields */}
      {loading && <div className="spf-loader">Loading bills data...</div>}
      {error && <div className="spf-error">{error}</div>}

      {!loading && !error && allSoldItems.length > 0 && (
        <div className="spf-table-section">
          <h2>Sold Products Data</h2>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
            📊 Products sold from CashBills, CreditBills, and Manual Entries
          </p>
          
          {/* Sold Products Summary */}
          <div className="sold-products-summary">
            <div className="summary-card">
              <h3>📈 Sales Summary</h3>
              <div className="summary-stats">
                <div className="stat-item">
                  <span className="stat-label">Total Sales Records:</span>
                  <span className="stat-value">{totalSoldProducts}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Quantity Sold:</span>
                  <span className="stat-value">{totalSoldQuantity.toLocaleString()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Sales Value:</span>
                  <span className="stat-value">₹{totalSoldValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
              </div>
              
              {/* Breakdown by Source */}
              <div className="source-breakdown">
                <h4>📊 Breakdown by Source</h4>
                <div className="source-stats">
                  {Object.entries(sourceTotals).map(([source, totals]) => (
                    <div key={source} className="source-item">
                      <div className="source-header">
                        <span className="source-name">
                          {source === 'CashBill' ? '💳 Cash Bills' : 
                           source === 'CreditBill' ? '📋 Credit Bills' : 
                           source === 'Manual Entry' ? '📝 Manual Entries' : source}
                        </span>
                      </div>
                      <div className="source-details">
                        <div className="source-detail">
                          <span>Records:</span>
                          <span>{totals.count}</span>
                        </div>
                        <div className="source-detail">
                          <span>Quantity:</span>
                          <span>{totals.quantity.toLocaleString()}</span>
                        </div>
                        <div className="source-detail">
                          <span>Value:</span>
                          <span>₹{totals.value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <table className="spf-table">
            <thead>
              <tr>
                <th>Source</th>
                <th>Invoice</th>
                <th>Customer</th>
                <th>Item Code</th>
                <th>Product Name</th>
                <th>HSN</th>
                <th>GST Rate (%)</th>
                <th>Quantity</th>
                <th>Rate (₹)</th>
                <th>Total (₹)</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {allSoldItems.map((item, index) => (
                <tr key={`${item.source}-${index}`}>
                  <td style={{ 
                    color: item.source === 'CashBill' ? 'green' : 
                           item.source === 'CreditBill' ? 'blue' : 
                           item.source === 'Manual Entry' ? 'orange' : 'purple',
                    fontWeight: 'bold'
                  }}>
                    {item.source === 'Manual Entry' ? '📝 Manual Entry' : item.source}
                  </td>
                  <td style={{ fontSize: '12px', color: '#666' }}>
                    {item.invoice}
                  </td>
                  <td style={{ fontSize: '12px', color: '#666' }}>
                    {item.customerName}
                  </td>
                  <td>{item.itemCode}</td>
                  <td>{item.itemName}</td>
                  <td>{item.hsn}</td>
                  <td>{item.gst}%</td>
                  <td>{item.quantity}</td>
                  <td style={{ fontWeight: 'bold', color: '#2c3e50' }}>
                    ₹{item.rate ? parseFloat(item.rate).toFixed(2) : '0.00'}
                  </td>
                  <td style={{ fontWeight: 'bold', color: '#27ae60' }}>
                    ₹{item.rate && item.quantity ? (parseFloat(item.rate) * parseFloat(item.quantity)).toFixed(2) : '0.00'}
                  </td>
                  <td style={{ fontSize: '11px', color: '#666' }}>
                    {item.date ? new Date(item.date).toLocaleDateString('en-IN') : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && allSoldItems.length === 0 && (
        <div className="spf-empty">
          <h3>📊 No Sold Products Found</h3>
          <p>No product sales data available. This could be because:</p>
          <ul style={{ textAlign: 'left', margin: '10px 0' }}>
            <li>• No cash bills or credit bills have been created yet</li>
            <li>• No manual sales have been recorded yet</li>
            <li>• The server is not running (check console for errors)</li>
          </ul>
          <p><strong>Tip:</strong> Try recording a manual sale using the form above!</p>
        </div>
      )}

    </div>
  );
};

export default SoldProductForm;
